import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import toast from "react-hot-toast";
import { KeyRound, LogOut, ShieldCheck } from "lucide-react";
import { Button } from "@veorest/ui";
import { fetchAdminMe, getSupabaseClient, useAuthStore } from "@veorest/api-client";
import { ROUTES } from "../../routes";
import { SettingsCard } from "./SettingsCard";

function friendlyPasswordError(err: unknown): string {
  const m = (err instanceof Error ? err.message : String(err ?? "")).toLowerCase();
  if (m.includes("weak") || m.includes("at least")) return "Password is too weak — use at least 8 characters.";
  if (m.includes("different from the old")) return "New password must differ from the current one.";
  if (m.includes("session")) return "Session expired — sign in again and retry.";
  return err instanceof Error ? err.message : "Could not update the password.";
}

/**
 * Security (Settings → Security) — password-level now:
 *  • admin account card (email/role via /v1/admin/me)
 *  • change password (Supabase auth.updateUser)
 *  • sign out from all devices (global sign-out)
 * Two-factor (TOTP) and platform-admin management are planned follow-ups.
 */
export function SecuritySettingsPanel() {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const me = useQuery({ queryKey: ["admin-me"], queryFn: fetchAdminMe, retry: false });

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [saving, setSaving] = useState(false);
  const [signingOut, setSigningOut] = useState(false);

  const changePassword = async () => {
    if (password.length < 8) {
      toast.error("Use at least 8 characters.");
      return;
    }
    if (password !== confirm) {
      toast.error("Passwords do not match.");
      return;
    }
    setSaving(true);
    try {
      const supabase = getSupabaseClient();
      const { error } = await supabase.auth.updateUser({ password });
      if (error) throw error;
      setPassword("");
      setConfirm("");
      toast.success("Password updated");
    } catch (e) {
      toast.error(friendlyPasswordError(e));
    } finally {
      setSaving(false);
    }
  };

  const signOutEverywhere = async () => {
    setSigningOut(true);
    try {
      const supabase = getSupabaseClient();
      await supabase.auth.signOut({ scope: "global" });
      navigate(ROUTES.LOGIN);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Could not sign out");
      setSigningOut(false);
    }
  };

  const lastSignIn = user?.last_sign_in_at
    ? new Date(user.last_sign_in_at).toLocaleString()
    : "—";

  return (
    <div className="space-y-4">
      <SettingsCard
        icon={ShieldCheck}
        title="Admin account"
        subtitle="Your platform administrator identity — role is managed in the platform_admins registry."
      >
        <div className="grid gap-4 md:grid-cols-3">
          <div>
            <p className="text-xs font-bold uppercase text-[var(--text-muted)]">Email</p>
            <p className="mt-1 text-sm font-bold break-all">{me.data?.email ?? user?.email ?? "—"}</p>
          </div>
          <div>
            <p className="text-xs font-bold uppercase text-[var(--text-muted)]">Role</p>
            <p className="mt-1 text-sm font-bold">
              {me.isLoading ? "…" : me.data?.role ?? "—"}
            </p>
          </div>
          <div>
            <p className="text-xs font-bold uppercase text-[var(--text-muted)]">Last sign-in</p>
            <p className="mt-1 text-sm font-bold">{lastSignIn}</p>
          </div>
        </div>
      </SettingsCard>

      <SettingsCard
        icon={KeyRound}
        title="Password"
        subtitle="Minimum 8 characters. You stay signed in on this device after changing it."
        actions={
          <Button className="min-h-[40px]" loading={saving} onClick={() => void changePassword()}>
            Update password
          </Button>
        }
      >
        <div className="grid gap-4 md:grid-cols-2">
          <label className="block space-y-1">
            <span className="text-sm font-bold">New password</span>
            <input
              className="input-base"
              type="password"
              autoComplete="new-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          </label>
          <label className="block space-y-1">
            <span className="text-sm font-bold">Confirm new password</span>
            <input
              className="input-base"
              type="password"
              autoComplete="new-password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
            />
          </label>
        </div>
      </SettingsCard>

      <SettingsCard
        icon={LogOut}
        title="Sessions"
        subtitle="End every active session for this account, including this one."
        actions={
          <Button
            variant="danger"
            className="min-h-[40px]"
            loading={signingOut}
            onClick={() => void signOutEverywhere()}
          >
            Sign out all devices
          </Button>
        }
        footnote="Next security steps (planned): two-factor TOTP and platform-admin management (invite / disable / roles)."
      >
        <p className="text-sm font-semibold text-[var(--text-secondary)]">
          Use this if you signed in on a shared or lost device. You will be asked to sign in again.
        </p>
      </SettingsCard>
    </div>
  );
}
